import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { EvinAnalysisResult } from '../../shared/types';
import { buildAnalysisPdfHtml } from './pdfHtml';

/**
 * Génère et télécharge un PDF du rapport d'analyse à la charte Evinly.
 *
 * Le HTML (buildAnalysisPdfHtml) est rendu dans une **iframe isolée** : son
 * document n'embarque aucune feuille de style Tailwind, donc aucune couleur
 * `oklch` que html2canvas 1.x ne sait pas parser (c'était la cause des échecs
 * qui faisaient retomber sur la boîte d'impression — laquelle supprime les
 * fonds de couleur). On capture ensuite une image raster : les cartes colorées
 * sont donc préservées, y compris à l'impression.
 *
 * Fallback ultime : si la capture échoue malgré tout, on ouvre la boîte
 * d'impression du navigateur sur le même HTML (print-color-adjust:exact).
 */
export async function downloadAnalysisPdf(
  result: EvinAnalysisResult,
  filename = 'analyse-evinly',
): Promise<void> {
  const generatedAt = new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const html = buildAnalysisPdfHtml(result, generatedAt);

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed',
    left: '-10000px',
    top: '0',
    width: '760px',
    height: '2200px',
    border: '0',
    background: '#ffffff',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument;
    if (!doc) throw new Error('iframe document indisponible');
    doc.open();
    doc.write(html);
    doc.close();

    // Attendre le chargement des polices (Atipla) + un tick de mise en page.
    try { await doc.fonts?.ready; } catch { /* fonts API absente */ }
    await new Promise((r) => setTimeout(r, 120));

    const blocks = Array.from(doc.querySelectorAll<HTMLElement>('.pdf-block'));
    if (blocks.length === 0) throw new Error('aucun bloc à exporter');

    const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 64;                 // marges blanches généreuses gauche/droite/haut/bas
    const contentW = pageW - margin * 2;
    const usableH = pageH - margin * 2;
    const gap = 18;                    // espace vertical entre blocs (respiration)
    let y = margin;

    for (const block of blocks) {
      const canvas = await html2canvas(block, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });
      const imgH = (canvas.height * contentW) / canvas.width;

      if (imgH <= usableH) {
        // Bloc entier — on saute à la page suivante s'il ne tient pas ici.
        if (y + imgH > pageH - margin) { pdf.addPage(); y = margin; }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', margin, y, contentW, imgH);
        y += imgH + gap;
      } else {
        // Bloc plus haut qu'une page : on le découpe sur plusieurs pages.
        if (y > margin) { pdf.addPage(); y = margin; }
        let srcY = 0;
        let remaining = imgH;
        while (remaining > 0.5) {
          const avail = pageH - margin - y;
          const sliceH = Math.min(avail, remaining);
          const srcSlicePx = Math.round((sliceH * canvas.width) / contentW);
          const srcYpx = Math.round((srcY * canvas.width) / contentW);
          const tmp = document.createElement('canvas');
          tmp.width = canvas.width;
          tmp.height = srcSlicePx;
          const ctx = tmp.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, tmp.width, tmp.height);
            ctx.drawImage(canvas, 0, srcYpx, canvas.width, srcSlicePx, 0, 0, canvas.width, srcSlicePx);
          }
          pdf.addImage(tmp.toDataURL('image/jpeg', 0.95), 'JPEG', margin, y, contentW, sliceH);
          remaining -= sliceH;
          srcY += sliceH;
          y += sliceH;
          if (remaining > 0.5) { pdf.addPage(); y = margin; }
        }
        y += gap;
      }
    }

    pdf.save(`${filename}.pdf`);
  } catch (err) {
    console.error('[pdf] capture failed, falling back to print dialog', err);
    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    }
  } finally {
    document.body.removeChild(iframe);
  }
}

/** Slugifie un titre pour en faire un nom de fichier sûr. */
export function pdfFilenameFor(title: string | undefined, fallback = 'analyse-evinly'): string {
  const base = (title ?? '').trim();
  if (!base) return fallback;
  const slug = base
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
  return slug ? `evinly-${slug}` : fallback;
}
